import React, { useState, useEffect, useRef } from "react";
import styles from "./Footer.module.scss";
import Link from "next/link";
import LaufidLogo from "@components/Icons/LaufidLogo/LaufidLogo";
import { useTranslations } from "next-intl";

const socials = [
    {
        icon: "/img/footer/linkedin.svg",
        url: "https://www.linkedin.com/company/laufid/",
    },
    {
        icon: "/img/footer/facebook.svg",
        url: "https://www.facebook.com/laufidiceland/",
    },
    {
        icon: "/img/footer/instagram.svg",
        url: "https://www.instagram.com/laufid.is/",
    },
    {
        icon: "/img/footer/spotify.svg",
        url: "https://open.spotify.com/show/32Nx41Hk4V4JYI8bnvTRYr",
    },
];

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= breakpoint);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, [breakpoint]);

    return isMobile;
}

function Footer() {
    const t = useTranslations("Header");
    const footerRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        if (footerRef.current) observer.observe(footerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 400);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <>
            <footer
                ref={footerRef}
                className={`${styles.container} ${isVisible ? styles.visible : ""}`}
            >
                <nav className={styles.contentContainer}>
                    <div className={styles.brandSection}>
                        <Link href="/" className={styles.logoWrapper}>
                            <LaufidLogo width="120px" color="#fff" />
                        </Link>
                        <p>
                            Akralind 6,
                            <br />
                            201 Kópavogur
                            <br />
                            <a href="mailto:laufid@laufid.is">
                                laufid@laufid.is
                            </a>
                        </p>
                    </div>

                    <FooterNavigationSection
                        title={t("solutions")}
                        items={[
                            {
                                title: t("companies"),
                                link: "/lausnir/fyrirtaeki",
                            },
                            {
                                title: t("public_entities"),
                                link: "/lausnir/opinberir-adilar",
                            },
                        ]}
                    />

                    <FooterNavigationSection
                        title={t("about_us")}
                        items={[
                            { title: t("about_us"), link: "/um-okkur" },
                            { title: t("contact_us"), link: "/hafa-samband" },
                            { title: t("pricelist"), link: "/verdskra" },
                        ]}
                    />

                    <FooterNavigationSection
                        title={t("customers")}
                        items={[
                            {
                                title: t("customers"),
                                link: "/vidskiptavinir",
                            },
                            {
                                title: t("privacy_policy"),
                                link: "/doc/personuvernd.pdf",
                                external: true,
                            },
                            {
                                title: t("terms_of_business"),
                                link: "/doc/vidskiptaskilmalar.pdf",
                                external: true,
                            },
                        ]}
                    />

                    <div className={styles.socialContainer}>
                        {socials.map((social, index) => (
                            <a
                                key={index}
                                href={social.url}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.socialLink}
                            >
                                <img
                                    className={styles.socialIcon}
                                    src={social.icon}
                                    alt=""
                                />
                            </a>
                        ))}
                    </div>
                </nav>

                <div className={styles.bottomBar}>
                    <p className={styles.copyright}>
                        {`© ${new Date().getFullYear()} Laufin ehf.`}
                    </p>
                </div>
            </footer>

            <button
                className={`${styles.backToTop} ${showBackToTop ? styles.backToTopVisible : ""}`}
                onClick={scrollToTop}
                aria-label="Back to top"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="18 15 12 9 6 15" />
                </svg>
            </button>
        </>
    );
}

function FooterNavigationSection({ title, items }) {
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);

    const showContent = !isMobile || isOpen;

    return (
        <div className={styles.navSection}>
            <h5
                className={isMobile ? styles.accordionToggle : undefined}
                onClick={isMobile ? () => setIsOpen(!isOpen) : undefined}
            >
                {title}
                {isMobile && (
                    <span
                        className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                )}
            </h5>
            <ul
                className={`${styles.navList} ${showContent ? styles.navListOpen : ""}`}
            >
                {items?.map?.((item, index) => (
                    <Link
                        key={index}
                        href={item.link}
                        target={item.external ? "_blank" : undefined}
                    >
                        <li>{item.title}</li>
                    </Link>
                ))}
            </ul>
        </div>
    );
}

export default Footer;
